import React from 'react';

import { ReviewProvider } from '@hydra/common/context/review';

import LayoutShell from '@hydra/common/shell/LayoutShell';

import ReviewPage from '@hydra/common/components/ReviewPage';
import ReviewGeneration from '@hydra/common/components/ReviewGeneration';
import ReviewMetaData from './ReviewMetaData';
import { ReviewInterface, ReviewStructure } from '@hydra/types/review';

import { useTheme, makeStyles } from '@material-ui/core/styles';
import { Theme } from '@material-ui/core/styles/createMuiTheme';
import useRecaptchaV3 from '@hydra/common/customHooks/useRecaptchaV3';

const useStyles = makeStyles(theme => ({
  root: { display: 'flex', paddingBottom: theme.spacing(3) }
}));

function parseBgColor (color: string, theme: Theme) {
  if (!color) {
    return theme.palette.background.default;
  }

  const parsedColor = color[0] === '#' ? color.replace('#', '').toUpperCase() : color.toUpperCase();
  const matchedColor = parsedColor.match(/[0-9A-F]/g);

  if (matchedColor && matchedColor.length === parsedColor.length) {
    return `#${parsedColor}`;
  }

  return theme.palette.background.default;
}

interface FreeReviewContainerProps {
  data: {
    review?: ReviewInterface
    reviewStructure?: ReviewStructure
    outdatedCriteria?: { [key in string]: number }
    url?: string
    generationId?: string
    bgColor?: string
    isExtension?: boolean
    isLeadgen?: boolean
  }
}

function FreeReviewContainer ({ data = {} }: FreeReviewContainerProps) {
  const theme = useTheme();
  const classes = useStyles();
  const {
    review,
    reviewStructure,
    outdatedCriteria = {},
    url,
    generationId,
    isExtension = false,
    isLeadgen = false
  } = data;
  const bgColor = isLeadgen
    ? parseBgColor(data.bgColor || '', theme)
    : theme.palette.grey[100];

  const { loadScript, monitorCaptchaScore } = useRecaptchaV3(isExtension ? 'free-review-extension' : 'free-review-leadgen', false);
  React.useEffect(() => {
    loadScript(function () {
      monitorCaptchaScore();
    });
  }, [loadScript, monitorCaptchaScore]);

  return (
    <ReviewProvider
      isFree
      isExtension={isExtension}
      isLeadgen={isLeadgen}
    >
      <ReviewMetaData url={url || ''} screenshot={(review && review.screenshot) || ''} isFree />
      <LayoutShell
        bgColor={bgColor}
        className={classes.root}
        sideNav={false}
      >
        {generationId
          ? <ReviewGeneration url={url} generationId={generationId} />
          : (
            <ReviewPage
              url={url}
              reviewStructure={reviewStructure}
              criteria={review && review.criteria}
              outdatedCriteria={outdatedCriteria}
              competitors={review && review.competitors}
              screenshot={review && review.screenshot}
              score={review && review.score}
              updatedAt={review && review.updatedAt}
              progress={review && review.progress}
              bgColor={bgColor}
            />
            )}
      </LayoutShell>
    </ReviewProvider>
  );
}

export default FreeReviewContainer;
